const apiBaseUrl = (() => {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();

  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && window.location.port === '3000') {
    return 'http://localhost:4000';
  }

  return '';
})();

const isLocalDevelopmentBrowser =
  typeof window !== 'undefined' && window.location.hostname === 'localhost' && window.location.port === '3000';
const requestTimeoutMs = 12000;

const withTimeoutSignal = () => {
  if (typeof AbortController === 'undefined') {
    return {
      signal: undefined,
      clear: () => {},
    };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);

  return {
    signal: controller.signal,
    clear: () => clearTimeout(timeoutId),
  };
};

const getServerConnectionErrorMessage = () =>
  isLocalDevelopmentBrowser
    ? 'Klarte ikke koble til admin-serveren. Start backend pa http://localhost:4000 og prov igjen.'
    : 'Klarte ikke koble til serveren. Sjekk at API og domene er riktig satt opp pa hosten.';

const getUnexpectedResponseMessage = () =>
  isLocalDevelopmentBrowser
    ? 'Klarte ikke laste innhold fra serveren. Sjekk at backend kjorer pa http://localhost:4000.'
    : 'Klarte ikke laste innhold fra serveren. Sjekk at nettsiden peker til riktig API i produksjon.';

const toApiUrl = (path) => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${apiBaseUrl}${path}`;
};

const fetchJson = async (url, options = {}) => {
  let response;
  const timeout = withTimeoutSignal();

  try {
    response = await fetch(toApiUrl(url), {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      signal: timeout.signal,
      ...options,
    });
  } catch (error) {
    throw new Error(getServerConnectionErrorMessage());
  } finally {
    timeout.clear();
  }

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    const payload = contentType.includes('application/json')
      ? await response.json().catch(() => ({ message: 'Ukjent feil' }))
      : {
          message: getUnexpectedResponseMessage(),
        };

    throw new Error(payload.message || 'Ukjent feil');
  }

  if (response.status === 204) {
    return null;
  }

  if (!contentType.includes('application/json')) {
    throw new Error(getUnexpectedResponseMessage());
  }

  return response.json();
};

const uploadFile = async (url, formData) => {
  let response;
  const timeout = withTimeoutSignal();

  try {
    response = await fetch(toApiUrl(url), {
      method: 'POST',
      credentials: 'include',
      signal: timeout.signal,
      body: formData,
    });
  } catch (error) {
    throw new Error(getServerConnectionErrorMessage());
  } finally {
    timeout.clear();
  }

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    const payload = contentType.includes('application/json')
      ? await response.json().catch(() => ({ message: 'Ukjent feil' }))
      : { message: getUnexpectedResponseMessage() };

    throw new Error(payload.message || 'Ukjent feil');
  }

  if (!contentType.includes('application/json')) {
    throw new Error(getUnexpectedResponseMessage());
  }

  return response.json();
};

const resolveMediaUrl = (path) => {
  if (!path) {
    return '';
  }

  return toApiUrl(path);
};

export const apiClient = {
  toApiUrl,
  fetchJson,
  uploadFile,
  resolveMediaUrl,
};